import { AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, inject, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { EmitterService, LAYOUT_RESIZED } from '@myia/ngx-core';
import { OnDestroyDirective } from '@myia/ngx-core-ui';
import { AppConfig } from '@shared';
import { fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'top-bar',
    styleUrls: ['./topBar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    hostDirectives: [
        OnDestroyDirective
    ],
    template: `
        <div class="topBarRoot" [ngClass]="{menu: showNavMenu}">
            <div class="content" [ngClass]="{withMenu: showButtonsDots}">
                <ng-content select="[content]"></ng-content>
            </div>
            <div class="buttonsWrapper" #buttonsWrapper>
                <div class="buttons" [ngClass]="{collapsed: showButtonsDots, menu: showNavMenu}">
                    <span *ngIf="isDebugModeEnabled" class="debugBlock">
                        <dev-tools *ngIf="showDevToolsMenu" (closed)="switchDevTools()"></dev-tools>
                        <button class="topBarButton withIcon debugTools" (click)="switchDevTools()"
                                tooltip="Developer tools (Debug only)" tooltipPlacement="bottom" tooltipClass="noWrap"
                                [tooltipOffset]="[0,10]" tooltipContainer=".topBarRoot">
                            <svg-icon name="bug"></svg-icon>
                            <label>Debugging tools</label></button>
                    </span>
                    <ng-content select="[buttons]"></ng-content>
                </div>
            </div>
            <button type="button" class="buttonsMenu" (click)="toggleMenu()"
                    [ngClass]="{collapsed: !showButtonsDots, new: showNewFlag}">
                <svg-icon name="dots"></svg-icon>
            </button>
        </div>
        <div class="notificationsContainer"></div>
    `,
})
export class TopBarComponent implements AfterViewInit, AfterViewChecked, OnInit, OnDestroy {

    @HostBinding('class.topBar') hostClass = true;

    @ViewChild('buttonsWrapper', { static: true }) buttonsWrapper?: ElementRef;

    @Input()
    showNewFlag = false;

    showNavMenu = false;
    showDevToolsMenu = false;
    isDebugModeEnabled = false;

    showButtonsDots: boolean = false;

    private _documentClickFunc?: Function;
    private _destroy$ = inject(OnDestroyDirective).destroy$;

    private onResizeBind: EventListener = this.onResize.bind(this);

    constructor(private _changeDetectorRef: ChangeDetectorRef, private _renderer: Renderer2) {
        this.isDebugModeEnabled = AppConfig.showDevTools;
    }

    ngAfterViewInit(): void {
        this.checkButtonsCollapseState();
    }

    ngAfterViewChecked(): void {
        this.checkButtonsCollapseState();
    }


    switchDevTools() {
        this.showDevToolsMenu = !this.showDevToolsMenu;
        this._changeDetectorRef.markForCheck();
    }

    toggleMenu() {
        this.showNavMenu = !this.showNavMenu;
        if (this.showNavMenu) {
            this.subscribeGlobalEvents();
        } else {
            this.unsubscribeGlobalEvents();
        }
    }

    ngOnInit() {
        fromEvent(window, 'resize').pipe(
            debounceTime(50),
            takeUntil(this._destroy$)
        ).subscribe(this.onResizeBind);
        EmitterService.getEvent(LAYOUT_RESIZED).pipe(
            takeUntil(this._destroy$)
        ).subscribe(this.onResizeBind);

    }

    ngOnDestroy() {
        this.unsubscribeGlobalEvents();
    }

    private subscribeGlobalEvents() {
        this.unsubscribeGlobalEvents();

        // handle click events on document to hide event list menu
        this._documentClickFunc = this._renderer.listen('document', 'click', (event) => {
            let current = event.target;
            while (current.tagName !== 'BODY' && current.tagName !== 'HTML') {
                if (current.classList.contains('buttonsMenu')) {
                    return;
                }
                current = current.parentNode;
                if (!current) {
                    // dom is not child of body (probably already removed from DOM)
                    return;
                }
            }
            this.unsubscribeGlobalEvents();
            this.showNavMenu = false;
            this._changeDetectorRef.markForCheck();
        });
    }

    private unsubscribeGlobalEvents() {
        if (this._documentClickFunc) {
            this._documentClickFunc();
            this._documentClickFunc = undefined;
        }
    }

    private onResize() {
        if (this.showNavMenu) {
            this.unsubscribeGlobalEvents();
            this.showNavMenu = false;
            this._changeDetectorRef.markForCheck();
        }
        this.checkButtonsCollapseState();
    }

    private checkButtonsCollapseState() {
        const showButtonsDots = this.buttonsWrapper?.nativeElement ? this.checkOverflow(this.buttonsWrapper.nativeElement) : false;
        if (this.showButtonsDots !== showButtonsDots) {
            this.showButtonsDots = showButtonsDots;
            this._changeDetectorRef.markForCheck();
            this._changeDetectorRef.detectChanges();
        }
    }


    private checkOverflow(el: HTMLElement) {
        const firstChild = el.firstChild as HTMLElement;
        const curWidth = el.style.width;
        firstChild.style.width = 'auto';
        const isOverflowing = el.clientWidth < el.scrollWidth;
        firstChild.style.width = curWidth;
        return isOverflowing;
    }
}


